/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
import HospitalDashboard from "layouts/hospital-dashboard";
import Tables from "layouts/tables";
import Billing from "layouts/billing";
import RTL from "layouts/rtl";
import Notifications from "layouts/notifications";
import Profile from "layouts/profile";
import TerminalUserSignIn from "layouts/authentication/terminal-sign-in";
import HospitalAdminUserBasic from "layouts/authentication/hospital-admin-sign-in";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import PermissionGroups from "layouts/configuration/permission-groups";
import Admins from "layouts/configuration/admins";
import PolicyItems from "layouts/configuration/policy-items";
import PolicyTypes from "layouts/configuration/policy-types";
import Terminals from "layouts/configuration/terminals";
import Hospitals from "layouts/configuration/hospitals";
import Cards from "layouts/cards";
import Policies from "layouts/policies";
import Insurees from "layouts/insurees";
import CoPays from "layouts/copays";
import Claims from "layouts/claims";
import TerminalClaims from "layouts/terminal/claims";
import HospitalClaims from "layouts/hospital/claims";
import RedirectIfAuthenticated from "components/Common/RedirectIfAuthenticated";
import RedirectIfTerminalAuthenticated from "components/Common/RedirectIfTerminalAuthenticated";
import ResetPassword from "layouts/authentication/reset-password";

// @mui icons
import Icon from "@mui/material/Icon";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import PeopleIcon from "@mui/icons-material/People";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import CategoryIcon from "@mui/icons-material/Category";
import QueueIcon from "@mui/icons-material/Queue";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CardIcon from "@mui/icons-material/CardMembership";
import DescriptionIcon from "@mui/icons-material/Description";
import DvrIcon from "@mui/icons-material/Dvr";
import GroupsIcon from "@mui/icons-material/Groups";
import CoPresentIcon from "@mui/icons-material/CoPresent";

const routes = [
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <AddIcon fontSize="small">login</AddIcon>,
    route: "/authentication/sign-in",
    component: (
      <RedirectIfAuthenticated>
        <SignIn />
      </RedirectIfAuthenticated>
    ),
  },
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Terminal User Sign In",
    key: "terminal-user-sign-in",
    icon: <AddIcon fontSize="small">login</AddIcon>,
    route: "/terminal/authentication/sign-in/:scCode",
    component: (
      <RedirectIfTerminalAuthenticated>
        <TerminalUserSignIn />
      </RedirectIfTerminalAuthenticated>
    ),
  },
  {
    addToMainMenu: false,
    type: "collapse",
    name: "Hospital Admin User Sign In",
    key: "hospital-user-sign-in",
    icon: <AddIcon fontSize="small">login</AddIcon>,
    route: "/hospital/authentication/sign-in/:scCode",
    component: (
      <RedirectIfTerminalAuthenticated>
        <HospitalAdminUserBasic />
      </RedirectIfTerminalAuthenticated>
    ),
  },
  {
    viewOn: ["ADMIN"],
    addToMainMenu: true,
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <DashboardIcon fontSize="small" />,
    route: "/dashboard",
    protected: true,
    component: <Dashboard />,
  },
  {
    viewOn: ["HOSPITAL ADMIN USER"],
    addToMainMenu: true,
    type: "collapse",
    name: "Dashboard",
    key: "hospitalDashboard",
    icon: <DashboardIcon fontSize="small" />,
    route: "/hospital-dashboard",
    protected: true,
    component: <HospitalDashboard />,
  },
  /*{
    addToMainMenu: true,
    type: "collapse",
    name: "Tables",
    key: "tables",
    icon: <Icon fontSize="small">table_view</Icon>,
    route: "/tables",
    component: <Tables />,
  },
  {
    addToMainMenu: true,
    type: "collapse",
    name: "Billing",
    key: "billing",
    icon: <Icon fontSize="small">receipt_long</Icon>,
    route: "/billing",
    component: <Billing />,
  },
  {
    addToMainMenu: true,
    type: "collapse",
    name: "RTL",
    key: "rtl",
    icon: <Icon fontSize="small">format_textdirection_r_to_l</Icon>,
    route: "/rtl",
    component: <RTL />,
  },*/
  {
    viewOn: ["ADMIN"],
    addToMainMenu: true,
    type: "collapse",
    name: "Claims",
    key: "claims",
    icon: <DescriptionIcon fontSize="small" />,
    route: "/claims",
    component: <Claims />,
  },
  {
    viewOn: ["TERMINAL USER"],
    addToMainMenu: true,
    type: "collapse",
    name: "Terminal Claims",
    key: "terminalclaims",
    icon: <DescriptionIcon fontSize="small" />,
    route: "/terminal/claims",
    component: <TerminalClaims />,
  },
  {
    viewOn: ["HOSPITAL ADMIN USER"],
    addToMainMenu: true,
    type: "collapse",
    name: "Hospital Claims",
    key: "hospitalclaims",
    icon: <DescriptionIcon fontSize="small" />,
    route: "/hospital/claims",
    component: <HospitalClaims />,
  },
  {
    viewOn: ["ADMIN"],
    addToMainMenu: true,
    type: "collapse",
    name: "Policies",
    key: "policies",
    icon: <DvrIcon fontSize="small" />,
    route: "/policies",
    component: <Policies />,
  },
  {
    viewOn: ["ADMIN"],
    addToMainMenu: true,
    type: "collapse",
    name: "Cards",
    key: "cards",
    icon: <CardIcon fontSize="small" />,
    route: "/cards",
    component: <Cards />,
  },
  {
    viewOn: ["ADMIN"],
    addToMainMenu: true,
    type: "collapse",
    name: "Insurees",
    key: "insurees",
    icon: <GroupsIcon fontSize="small" />,
    route: "/insurees",
    component: <Insurees />,
  },
  {
    viewOn: ["ADMIN"],
    addToMainMenu: true,
    type: "collapse",
    name: "Co-Pays",
    key: "copays",
    icon: <CoPresentIcon fontSize="small" />,
    route: "/copays",
    component: <CoPays />,
  },
  {
    viewOn: ["ADMIN"],
    addToMainMenu: true,
    type: "collapse",
    name: "Configuration",
    key: "settings",
    protected: true,
    icon: <MenuIcon fontSize="small" />,
    collapse: [
      {
        viewOn: ["ADMIN"],
        addToMainMenu: true,
        type: "collapse",
        collapse: [],
        name: "Policy Items",
        key: "policy-items",
        icon: <CategoryIcon fontSize="small" />,
        route: "/configuration/policy-items",
        permission: "VIEW POLICY ITEMS",
        protected: true,
        component: <PolicyItems />,
      },
      {
        viewOn: ["ADMIN"],
        addToMainMenu: true,
        type: "collapse",
        collapse: [],
        name: "Policy Types",
        key: "policy-types",
        icon: <QueueIcon fontSize="small" />,
        route: "/configuration/policy-types",
        permission: "VIEW POLICY TYPES",
        protected: true,
        component: <PolicyTypes />,
      },
      {
        viewOn: ["ADMIN"],
        addToMainMenu: true,
        type: "collapse",
        collapse: [],
        name: "Hospitals",
        key: "hospitals",
        icon: <LocalHospitalIcon fontSize="small" />,
        route: "/configuration/hospitals",
        permission: "VIEW HOSPITALS",
        protected: true,
        component: <Hospitals />,
      },
      {
        viewOn: ["ADMIN"],
        addToMainMenu: true,
        type: "collapse",
        collapse: [],
        name: "Terminals",
        key: "terminals",
        icon: <BrowserUpdatedIcon fontSize="small" />,
        route: "/configuration/terminals",
        permission: "VIEW TERMINALS",
        protected: true,
        component: <Terminals />,
      },
      {
        viewOn: ["ADMIN"],
        addToMainMenu: true,
        type: "collapse",
        collapse: [],
        name: "Permission Groups",
        key: "permission-groups",
        icon: <GroupWorkIcon fontSize="small" />,
        route: "/configuration/permission-groups",
        permission: "VIEW PERMISSION GROUP",
        protected: true,
        component: <PermissionGroups />,
      },
      {
        viewOn: ["ADMIN"],
        addToMainMenu: true,
        type: "collapse",
        collapse: [],
        name: "Admin Users",
        key: "admin-users",
        icon: <PeopleIcon fontSize="small" />,
        route: "/configuration/admin",
        permission: "VIEW ADMINS",
        protected: true,
        component: <Admins />,
      },
    ],
  },
  {
    viewOn: ["ADMIN"],
    addToMainMenu: false,
    type: "collapse",
    name: "Profile",
    key: "profile",
    protected: true,
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: <Profile />,
  },
  {
    viewOn: ["ADMIN"],
    addToMainMenu: false,
    type: "collapse",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
  },
  {
    viewOn: ["ADMIN"],
    addToMainMenu: false,
    type: "collapse",
    name: "Reset Password",
    key: "sign-up",
    icon: <Icon fontSize="small">password</Icon>,
    route: "/authentication/reset-password",
    component: <ResetPassword />,
  },
];

export default routes;
